import React from 'react';
import PropTypes from 'prop-types';
import { formatCost, formatLatency } from '../../utils/formatters';
import { getModelDisplayName } from '../../constants/models';
import ComparisonPerformanceChart from './ComparisonPerformanceChart';
import ComparisonWinRateChart from './ComparisonWinRateChart';
import ComparisonCostChart from './ComparisonCostChart';
import ComparisonTokenChart from './ComparisonTokenChart';

const ModelComparisonStats = ({ stats, breakdown, recentComparisons, loading }) => {
  if (loading) {
    return (
      <div className="space-y-4">
        {/* Loading Stats Cards */}
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div 
              key={i} 
              className="rounded-lg p-4 animate-pulse bg-surface-primary/60 border border-border-secondary"
            >
              <div className="h-3 rounded w-20 mb-2 bg-success-50" />
              <div className="h-6 rounded w-16 bg-success-50" />
            </div>
          ))}
        </div>
        {/* Loading Table */}
        <div className="rounded-lg overflow-hidden bg-surface-primary/60 border border-border-secondary">
          <div className="px-6 py-4 border-b border-border-secondary">
            <div className="h-4 rounded w-32 bg-success-50" />
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-12 rounded bg-primary-500/5" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!stats && !breakdown && (!recentComparisons || recentComparisons.length === 0)) {
    return (
      <div className="rounded-lg overflow-hidden bg-surface-primary/60 border border-border-secondary">
        <div className="px-6 py-16 text-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-success-50 border-2 border-primary-500/20">
            <svg className="h-8 w-8 text-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="18" rx="1" />
              <rect x="14" y="3" width="7" height="18" rx="1" />
              <path d="M12 8V16" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="2 2" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold mb-1 text-text-primary">No Comparisons Yet</h3>
          <p className="text-xs text-text-tertiary">
            Model comparison statistics will appear here once you start comparing models
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Overview Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Total Comparisons', value: stats.total_comparisons || 0 },
            { label: 'Success Rate', value: `${stats.success_rate?.toFixed(1) || 0}%` },
            { label: 'Total Tokens', value: (stats.total_tokens || 0).toLocaleString() },
            { label: 'Total Cost', value: formatCost(stats.total_cost || 0) }
          ].map((stat, i) => (
            <div 
              key={i}
              className="rounded-lg p-4 transition-all duration-200 bg-surface-primary/60 border border-border-secondary hover:border-primary-500/25 hover:shadow-glow-sm"
            >
              <dt className="text-xs mb-1.5 text-text-tertiary">{stat.label}</dt>
              <dd className="text-xl font-semibold text-text-primary">{stat.value}</dd>
            </div>
          ))}
        </div>
      )}

      {/* Charts Section - Row 1 */}
      {recentComparisons?.comparisons && recentComparisons.comparisons.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ComparisonPerformanceChart
            comparisons={recentComparisons.comparisons}
            loading={loading}
          />
          <ComparisonWinRateChart
            comparisons={recentComparisons.comparisons}
            loading={loading}
          />
        </div>
      )}

      {/* Charts Section - Row 2 */}
      {recentComparisons?.comparisons && recentComparisons.comparisons.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ComparisonCostChart
            comparisons={recentComparisons.comparisons}
            loading={loading}
          />
          <ComparisonTokenChart
            comparisons={recentComparisons.comparisons}
            loading={loading}
          />
        </div>
      )}

      {/* Model Pair Breakdown */}
      {breakdown && breakdown.pairs && breakdown.pairs.length > 0 && (
        <div className="rounded-lg overflow-hidden bg-surface-primary/60 border border-border-secondary">
          <div className="px-6 py-4 border-b border-border-secondary">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-text-primary">Popular Model Comparisons</h3>
              <span className="text-xs text-text-tertiary">
                {breakdown.pairs.length} {breakdown.pairs.length === 1 ? 'pair' : 'pairs'}
              </span>
            </div>
          </div>
          <div className="overflow-hidden">
            <div className="max-h-[500px] overflow-y-auto overflow-x-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-primary-500/30 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-primary-500">
              <table className="min-w-full border-separate border-spacing-0">
                <thead className="sticky top-0 z-10 bg-bg-secondary/95">
                  <tr>
                    {['Model Pair', 'Comparisons', 'Avg Latency', 'Avg Cost', 'Winner'].map((header, i) => (
                      <th 
                        key={i}
                        scope="col" 
                        className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-text-tertiary border-b border-border-secondary ${i === 1 || i === 4 ? 'text-center' : i > 1 ? 'text-right' : 'text-left'}`}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {breakdown.pairs.map((pair, index) => {
                    const model1Faster = (pair.avg_latency_model_1 || 0) < (pair.avg_latency_model_2 || 0);
                    const model1Cheaper = (pair.avg_cost_model_1 || 0) < (pair.avg_cost_model_2 || 0);
                    const model1Wins = model1Faster && model1Cheaper;
                    const model2Wins = !model1Faster && !model1Cheaper;
                    
                    return (
                      <tr 
                        key={index} 
                        className={`transition-colors cursor-pointer hover:bg-primary-500/5 ${
                          index < breakdown.pairs.length - 1 ? 'border-b border-primary-500/5' : ''
                        }`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="inline-block px-2 py-0.5 text-xs font-medium font-mono rounded bg-info-50 text-info border border-info/30">
                              {getModelDisplayName(pair.model_1)}
                            </span>
                            <span className="text-xs text-text-tertiary">vs</span>
                            <span className="inline-block px-2 py-0.5 text-xs font-medium font-mono rounded bg-success-50 text-primary-500 border border-primary-500/30">
                              {getModelDisplayName(pair.model_2)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="text-sm font-semibold text-text-primary">{pair.count}</span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="space-y-1">
                            <div className={`text-sm font-mono ${model1Faster ? 'text-primary-500 font-semibold' : 'text-text-secondary'}`}>
                              {formatLatency(pair.avg_latency_model_1 || 0)}
                            </div>
                            <div className={`text-sm font-mono ${!model1Faster ? 'text-primary-500 font-semibold' : 'text-text-secondary'}`}>
                              {formatLatency(pair.avg_latency_model_2 || 0)}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="space-y-1">
                            <div className={`text-sm font-mono ${model1Cheaper ? 'text-primary-500 font-semibold' : 'text-text-secondary'}`}>
                              {formatCost(pair.avg_cost_model_1 || 0)}
                            </div>
                            <div className={`text-sm font-mono ${!model1Cheaper ? 'text-primary-500 font-semibold' : 'text-text-secondary'}`}>
                              {formatCost(pair.avg_cost_model_2 || 0)}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {model1Wins && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-50 text-primary-500 border border-primary-500/30">
                              {getModelDisplayName(pair.model_1)}
                            </span>
                          )}
                          {model2Wins && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-50 text-primary-500 border border-primary-500/30">
                              {getModelDisplayName(pair.model_2)}
                            </span>
                          )}
                          {!model1Wins && !model2Wins && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-500/15 text-gray-400 border border-gray-500/30">
                              Mixed
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Recent Comparisons */}
      {recentComparisons && recentComparisons.comparisons && recentComparisons.comparisons.length > 0 && (
        <div className="rounded-lg overflow-hidden bg-surface-primary/60 border border-border-secondary">
          <div className="px-6 py-4 border-b border-border-secondary">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-text-primary">Recent Comparisons</h3>
              <span className="text-xs text-text-tertiary">{recentComparisons.total_count} total</span>
            </div>
          </div>
          <div className="overflow-hidden">
            <div className="max-h-[500px] overflow-y-auto overflow-x-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-primary-500/30 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-primary-500">
              <table className="min-w-full border-separate border-spacing-0">
                <thead className="sticky top-0 z-10 bg-bg-secondary/95">
                  <tr>
                    {['Comparison Name', 'Models', 'Status', 'Latency', 'Cost', 'Time'].map((header, i) => (
                      <th 
                        key={i}
                        scope="col" 
                        className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-text-tertiary border-b border-border-secondary ${i === 2 ? 'text-center' : i > 2 ? 'text-right' : 'text-left'}`}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recentComparisons.comparisons.slice(0, 10).map((comp, index) => (
                    <tr 
                      key={comp.id} 
                      className={`transition-colors cursor-pointer hover:bg-primary-500/5 ${
                        index < 9 ? 'border-b border-primary-500/5' : ''
                      }`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-text-primary">{comp.comparison_name}</span>
                          {comp.project_name && (
                            <span className="text-xs mt-0.5 text-text-tertiary">{comp.project_name}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          <span className="inline-block px-2 py-0.5 text-xs font-medium font-mono rounded w-fit bg-info-50 text-info border border-info/30">
                            {getModelDisplayName(comp.model_1)}
                          </span>
                          <span className="inline-block px-2 py-0.5 text-xs font-medium font-mono rounded w-fit bg-success-50 text-primary-500 border border-primary-500/30">
                            {getModelDisplayName(comp.model_2)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex flex-col gap-1 items-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                            comp.status_1 === 'success' 
                              ? 'bg-success-50 text-primary-500 border-primary-500/30'
                              : 'bg-error-50 text-error border-error/30'
                          }`}>
                            {comp.status_1}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                            comp.status_2 === 'success' 
                              ? 'bg-success-50 text-primary-500 border-primary-500/30'
                              : 'bg-error-50 text-error border-error/30'
                          }`}>
                            {comp.status_2}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="space-y-1 text-sm font-mono text-text-secondary">
                          <div>{formatLatency(comp.latency_ms_1 || 0)}</div>
                          <div>{formatLatency(comp.latency_ms_2 || 0)}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="space-y-1 text-sm font-mono text-primary-500">
                          <div>{formatCost(comp.estimated_cost_1 || 0)}</div>
                          <div>{formatCost(comp.estimated_cost_2 || 0)}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-xs text-text-tertiary">
                          {new Date(comp.created_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ModelComparisonStats.propTypes = {
  stats: PropTypes.shape({
    total_comparisons: PropTypes.number,
    success_rate: PropTypes.number,
    total_tokens: PropTypes.number,
    total_cost: PropTypes.number
  }),
  breakdown: PropTypes.shape({
    pairs: PropTypes.array
  }),
  recentComparisons: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.shape({
      comparisons: PropTypes.array,
      total_count: PropTypes.number,
      length: PropTypes.number
    })
  ]),
  loading: PropTypes.bool
};

export default ModelComparisonStats;
